/**
 * TrustGate Router - React Router Configuration
 * Routes console views to URL paths
 *
 * ROUTING ARCHITECTURE NOTES:
 * - This file handles NESTED routing for /console/* routes
 * - It is rendered by main-trustgate.tsx under the /console/* path (see line ~455)
 * - All routes here use RELATIVE paths (e.g., "proof-explorer" not "/console/proof-explorer")
 * - Do NOT add BrowserRouter here - it's already provided in main-trustgate.tsx (Root component, line ~265)
 * - For console route changes, edit this file
 * - For top-level route changes (verify, governance, marketplace), edit main-trustgate.tsx
 * - Navigation helpers use useNavigate() with relative paths for nested routes
 */

import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { ConsoleHome } from './ConsoleHome';
import { ProofExplorer } from './ProofExplorer';
import { WorkflowRunner } from './WorkflowRunner';
import { CivilDisclosureWorkspace } from './CivilDisclosureWorkspace';
import { PaperCollaborationWorkspace } from './PaperCollaborationWorkspace';
import { GuidedRequestWizard, FlowSelector, getFlowPreset, type FlowPreset } from '../guide';
import { subscribeActivePolicyPack, type ActivePolicyPack } from '../policy_packs/PolicyPacksService';
import SecurityDashboard from '../../security/SecurityDashboard';
import RouterErrorBoundary from './RouterErrorBoundary';
import { FirestoreErrorBoundary } from './components/FirestoreErrorBoundary';
import { PdfEvidenceBundle } from '../../components/PdfEvidenceBundle';
import { OperationalBundleViewerRoute } from '../routes/OperationalBundleViewerRoute.js';
import { SemanticIntegrityDashboard } from '../semanticIntegrity/SemanticIntegrityDashboard';
import { VK1ArchitectureVisualization } from './VK1ArchitectureVisualization';
import type { ContentArtifactV1 } from '../modules/workspace/ingestion/contentArtifact';
import type { ChunkLink } from '../modules/workspace/connectors/pdf/crossDocumentChunkLinker';
import type { User } from 'firebase/auth';

interface TrustGateRouterProps {
  user: User;
  isAdmin: boolean;
  onNavigateToAdmin?: () => void;
}

// Wrapper component to handle preset selection from URL params
function CreateRequestWrapper({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetId = searchParams.get('preset_id');
  const [selectedPreset, setSelectedPreset] = useState<FlowPreset | null>(null);

  useEffect(() => {
    if (presetId) {
      const preset = getFlowPreset(presetId);
      setSelectedPreset(preset);
    }
  }, [presetId]);

  if (!selectedPreset) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-[#1a1a2e] border border-[#f59e0b]/30 rounded-xl p-8 text-center">
          <div className="text-[#f59e0b] text-2xl font-semibold mb-3">No Flow Preset Selected</div>
          <div className="text-[#cbd5e1] text-sm mb-6">
            Please select a flow template from the flow selector.
          </div>
          <button
            onClick={() => navigate('/console/flow-selector')}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            Select Flow Template
          </button>
        </div>
      </div>
    );
  }

  return (
    <GuidedRequestWizard
      preset={selectedPreset}
      userId={userId}
      onComplete={(requestId) => {
        console.log('✅ Request submitted:', requestId);
        navigate('/console/proof-explorer');
      }}
      onCancel={() => navigate('/console/flow-selector')}
    />
  );
}

function ConsoleHomeWrapper({ user, isAdmin, onNavigateToAdmin }: { user: User; isAdmin: boolean; onNavigateToAdmin?: () => void }) {
  const navigate = useNavigate();

  return (
    <FirestoreErrorBoundary>
      <ConsoleHome
        tenantId={user.uid}
        isAdmin={isAdmin}
        onNavigateToProofExplorer={() => navigate('proof-explorer')}
        onNavigateToAdmin={onNavigateToAdmin}
        onNavigateToCreateRequest={() => navigate('flow-selector')}
        onNavigateToPolicyPacks={() => navigate('/policy-packs')}
        onNavigateToCivilDisclosure={() => navigate('civil-disclosure')}
        onNavigateToPaperCollaboration={() => navigate('paper-collaboration')}
        onNavigateToPdfEvidenceBundle={() => navigate('pdf-evidence-bundle')}
        onNavigateToVK1Architecture={() => navigate('vk1-architecture')}
      />
    </FirestoreErrorBoundary>
  );
}

function ProofExplorerWrapper({ isAdmin }: { isAdmin: boolean }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request_id');

  // TODO: If requestId is provided, auto-load the request in the explorer
  // This requires updating ProofExplorer to accept an initial request ID prop
  console.debug('ProofExplorer: requestId from URL params:', requestId); // Suppress unused warning

  return <ProofExplorer isAdmin={isAdmin} onNavigateBack={() => navigate('')} />;
}

function WorkflowRunnerWrapper() {
  const navigate = useNavigate();
  // const [searchParams] = useSearchParams();
  // const workflowId = searchParams.get('workflow_id');

  // TODO: If workflowId is provided, auto-load the workflow in the runner
  // This requires updating WorkflowRunner to accept an initial workflow ID prop

  return <WorkflowRunner onNavigateBack={() => navigate('')} />;
}

function CivilDisclosureWrapper({ user }: { user: User }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get('module_id');

  // TODO: If moduleId is provided, auto-load the module in the workspace
  // This requires updating CivilDisclosureWorkspace to accept an initial module ID prop
  console.debug('CivilDisclosure: moduleId from URL params:', moduleId); // Suppress unused warning

  return <CivilDisclosureWorkspace user={user} onNavigateBack={() => navigate('')} />;
}

function PaperCollaborationWrapper({ user }: { user: User }) {
  const navigate = useNavigate();
  return <PaperCollaborationWorkspace user={user} onNavigateBack={() => navigate('')} />;
}

function PdfEvidenceBundleWrapper() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // TODO: If artifact IDs are provided in URL params, load them
  // This would require passing artifact data from a parent component or fetching from Firestore
  const artifactsParam = searchParams.get('artifacts');
  const linksParam = searchParams.get('links');
  
  // For now, pass empty arrays - the component will be in a state where user can load artifacts
  const artifacts: ContentArtifactV1[] = [];
  const crossDocumentLinks: ChunkLink[] = [];
  
  return (
    <div className="max-w-6xl mx-auto p-8">
      <button
        onClick={() => navigate('')}
        className="mb-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
      >
        ← Back to Console
      </button>
      <PdfEvidenceBundle artifacts={artifacts} crossDocumentLinks={crossDocumentLinks} />
    </div>
  );
}

function SemanticIntegrityWrapper() {
  const navigate = useNavigate();

  return (
    <div>
      <button
        onClick={() => navigate('')}
        className="mb-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
      >
        ← Back to Console
      </button>
      <SemanticIntegrityDashboard />
    </div>
  );
}

function VK1ArchitectureWrapper() {
  const navigate = useNavigate();

  return (
    <div>
      <button
        onClick={() => navigate('')}
        className="mb-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
      >
        ← Back to Console
      </button>
      <VK1ArchitectureVisualization />
    </div>
  );
}

// Wrapper for FlowSelector to handle policy pack check
function FlowSelectorWrapper({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const [activePolicyPack, setActivePolicyPack] = useState<ActivePolicyPack | null>(null);
  const [activePolicyPackLoading, setActivePolicyPackLoading] = useState(true);
  const [activePolicyPackError, setActivePolicyPackError] = useState<string | null>(null);

  useEffect(() => {
    setActivePolicyPackLoading(true);
    setActivePolicyPackError(null);

    return subscribeActivePolicyPack(
      userId,
      (value) => {
        setActivePolicyPack(value);
        setActivePolicyPackLoading(false);
      },
      (error) => {
        setActivePolicyPack(null);
        setActivePolicyPackError(error.message || 'Failed to load active policy pack');
        setActivePolicyPackLoading(false);
      }
    );
  }, [userId]);

  const guidedFlowBlocked = activePolicyPackLoading || !!activePolicyPackError || !activePolicyPack;

  if (guidedFlowBlocked) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-[#1a1a2e] border border-red-500/30 rounded-xl p-8">
          <div className="text-center mb-6">
            <div className="text-[#ef4444] text-2xl font-semibold mb-3">
              {activePolicyPackError ? 'Governance State Unavailable' : 'No Active Governing Policy'}
            </div>
            <div className="text-[#cbd5e1] text-sm mb-6">
              {activePolicyPackError
                ? 'The active governing policy pack could not be loaded. Guided request creation is fail-closed until governance state is readable.'
                : activePolicyPackLoading
                ? 'Loading active governance state before enabling guided request creation.'
                : 'This tenant does not currently have an active policy pack. Guided request creation is fail-closed until governance is configured.'}
            </div>
          </div>

          {/* Enhanced Guidance Section */}
          <div className="space-y-4 mb-8">
            <div className="bg-[#0a0a0f]/50 border border-white/10 rounded-lg p-4">
              <h4 className="text-white font-semibold mb-2">Why is this required?</h4>
              <p className="text-gray-300 text-sm">
                Guided requests require governance policies to ensure safety, compliance, and proper authority validation. 
                Policy packs define the rules and boundaries for all automated actions.
              </p>
            </div>

            <div className="bg-[#0a0a0f]/50 border border-white/10 rounded-lg p-4">
              <h4 className="text-white font-semibold mb-2">Next Steps</h4>
              <div className="space-y-2 text-sm text-gray-300">
                <div className="flex items-center gap-2">
                  <span className="text-[#00d4aa]">1.</span>
                  <span>Configure an active policy pack for your tenant</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#00d4aa]">2.</span>
                  <span>Choose industry-specific templates that match your needs</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#00d4aa]">3.</span>
                  <span>Submit governed requests with confidence</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate('')}
              className="w-full sm:w-auto px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              Back to Console
            </button>
            <button
              onClick={() => navigate('/policy-packs')}
              className="w-full sm:w-auto px-6 py-3 bg-[#00d4aa]/10 hover:bg-[#00d4aa]/20 border border-[#00d4aa]/30 text-[#00d4aa] rounded-lg transition-colors font-medium"
            >
              Configure Policy Packs
            </button>
            {activePolicyPackError && (
              <button
                onClick={() => window.location.reload()}
                className="w-full sm:w-auto px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
              >
                Retry Loading
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <FlowSelector
      onSelectFlow={(preset) => {
        navigate(`create-request?preset_id=${preset.id}`);
      }}
      onCancel={() => navigate('')}
    />
  );
}

export const TrustGateRouter: React.FC<TrustGateRouterProps> = ({
  user,
  isAdmin,
  onNavigateToAdmin,
}) => {
  return (
    <RouterErrorBoundary>
      <Routes>
          {/* Console Routes - relative paths since nested under /console/* */}
          <Route path="" element={<ConsoleHomeWrapper user={user} isAdmin={isAdmin} onNavigateToAdmin={onNavigateToAdmin} />} />
          <Route path="proof-explorer" element={<ProofExplorerWrapper isAdmin={isAdmin} />} />
          <Route path="workflow-runner" element={<WorkflowRunnerWrapper />} />
          <Route path="civil-disclosure" element={<CivilDisclosureWrapper user={user} />} />
          <Route path="paper-collaboration" element={<PaperCollaborationWrapper user={user} />} />
          <Route path="flow-selector" element={<FlowSelectorWrapper userId={user.uid} />} />
          <Route path="create-request" element={<CreateRequestWrapper userId={user.uid} />} />
          <Route path="security" element={<SecurityDashboard />} />
          <Route path="semantic-integrity" element={<SemanticIntegrityWrapper />} />
          <Route path="vk1-architecture" element={<VK1ArchitectureWrapper />} />
          <Route path="pdf-evidence-bundle" element={<PdfEvidenceBundleWrapper />} />
          <Route path="security-test" element={<SecurityDashboard />} />
          <Route path="verify/operational-bundle" element={<OperationalBundleViewerRoute />} />

          {/* Default redirect to console home */}
          <Route path="*" element={<Navigate to="" replace />} />
        </Routes>
      </RouterErrorBoundary>
  );
};
